import { batch, createResource, createSignal, createMemo, For, children } from "solid-js";
import { render } from "solid-js/web";
import { createListTransition } from "@solid-primitives/transition-group";
import { createResourceWithInitialData } from "./utility";

let cachedCategory = null;
let cachedEntries = [];

const [isPageLoad, setIsPageLoad] = createSignal(false);
const [pageCount, setPageCount] = createSignal(0);
const [page, setPage] = createSignal(1);

async function fetchEntries({ categoryId, page }) {
    let query = new URLSearchParams();
    query.set("per_page", 24);
    query.set("status", "publish");
    query.set("order", "desc");
    query.set("orderby", "modified");
    query.set("lang", document.documentElement.lang.split("-")[0]);

    let pageLoad = false;
    if (cachedCategory != categoryId) {
        pageLoad = false;
        cachedEntries = [];
        page = 1;
    } else {
        pageLoad = true;
    }

    query.set("page", page);

    batch(() => {
        setIsPageLoad(pageLoad);
        if (!pageLoad) {
            setPage(1);
        }
    });

    cachedCategory = categoryId;

    if (categoryId != 0) {
        query.set("portfolio_category", categoryId);
    }

    //

    const response = await fetch(`/wp-json/wp/v2/portfolio?${query.toString()}`);
    if (!response.ok) {
        console.error("API Error:", response);
        return cachedEntries;
    }

    const totalPages = parseInt(response.headers.get("x-wp-totalpages"));

    setPageCount(totalPages);

    const items = await response.json();
    const mediaIds = items.map(i => i.featured_media);

    const mediasResponse = await fetch(`/wp-json/wp/v2/media?per_page=100&include=${mediaIds.join(",")}`);
    const medias = await mediasResponse.json();

    const reformattedItems = items.map(i => {
        let thumbnail = null;

        for (const media of medias) {
            if (media.id == i.featured_media) {
                thumbnail = media.media_details.sizes.medium?.source_url ?? null;
                break;
            }
        }

        return {
            thumbnail,
            ...i
        };
    });

    cachedEntries.push(...reformattedItems);
    return [...cachedEntries];
}

export function CategoryListAnimation(props) {
    const resolved = children(() => props.children);
    const filtered = createMemo(() => resolved.toArray().filter(el => el instanceof HTMLElement));

    const { enterClass, exitClass } = props;

    const transition = createListTransition(filtered, {
        appear: true,
        exitMethod: "keep-index",
        onChange({ added, removed, finishRemoved }) {
            const layout = [];

            for (const element of removed) {
                const rect = element.getBoundingClientRect();
                const parentRect = element.parentNode.getBoundingClientRect();
                layout.push({
                    x: rect.x - parentRect.x,
                    w: rect.width,
                });
            }

            for (const [i, element] of removed.entries()) {
                element.style.left = `${layout[i].x}px`;
                element.style.width = `${layout[i].w}px`;
                element.classList.add(...exitClass.split(" "));
            }

            queueMicrotask(() => {
                for (const element of added) {
                    element.classList.add(...enterClass.split(" "));
                }
            });

            if (removed.length > 0) {
                removed[0].addEventListener("animationend", () => {
                    finishRemoved(removed);
                });
            }
        },
    });

    return transition;
}

export function Archive({ categories, categoryTree, initialPosts }) {
    let categoryFilterPath = (window.location.hash || "#").slice(1);

    const [categoryFilterSegments, setCategoryFilterSegments] = createSignal(categoryFilterPath.split("/").filter(i => i != ""));

    let selectedCategory = categoryTree;
    for (const segment of categoryFilterSegments()) {
        selectedCategory = selectedCategory[segment];
    }
    let categoryFilterId = selectedCategory._id ?? 0;

    cachedCategory = categoryFilterId;

    const [currentCategory, setCurrentCategory] = createSignal(categoryFilterId);

    window.addEventListener("hashchange", (e) => {
        e.preventDefault();

        batch(() => {
            categoryFilterPath = (window.location.hash || "#").slice(1);
            setCategoryFilterSegments(categoryFilterPath.split("/").filter(i => i != ""));

            let selectedCategory = categoryTree;
            for (const segment of categoryFilterSegments()) {
                selectedCategory = selectedCategory[segment];
            }
            categoryFilterId = selectedCategory._id ?? 0;

            setCurrentCategory(categoryFilterId);
            setPage(1);
        });
    });

    let entries;

    if (categoryFilterId == 0) {
        cachedEntries = initialPosts;
        [entries] = createResourceWithInitialData(fetchEntries, initialPosts, () => {
            return {
                categoryId: currentCategory() ?? 0,
                page: page(),
            };
        });
    } else {
        [entries] = createResource(() => {
            return {
                categoryId: currentCategory() ?? 0,
                page: page(),
            };
        }, fetchEntries);
    }

    function onCategoryClick(category, e) {
        e.preventDefault();
        const hashBuilder = [];

        if (!window.location.hash || window.location.hash == "") {
            hashBuilder.push("#");
        } else if (category.parent != 0) {
            hashBuilder.push(window.location.hash);
        }

        hashBuilder.push("/", category.slug);

        setPage(1);
        window.location.hash = hashBuilder.join("");
    }

    function backButtonLabel() {
        let segments = categoryFilterSegments();
        return `< ${ categories.find(i => i.slug == segments[segments.length-1])?.name ?? "" }`;
    }

    function previousUrl() {
        let segments = categoryFilterSegments();
        return "#" + segments.slice(0, segments.length-1).join("/");
    }

    let infiniteScrollTickHandle = null;
    function tickInfiniteScroll() {
        const newPage = page() + 1;
        if (pageCount() > 0 && pageCount() >= newPage) {
            setPage(newPage);
        }
    }

    function infiniteScrollCallback(e, _) {
        if (e[0].intersectionRatio > 0 && entries.loading == false) {
            const newPage = page() + 1;
            if (pageCount() > 0 && pageCount() >= newPage || pageCount() == 0) {
                setPage(newPage);
            }

            infiniteScrollTickHandle = setInterval(tickInfiniteScroll, 750);
        } else if (e[0].intersectionRatio === 0) {
            clearInterval(infiniteScrollTickHandle);
        }
    }

    function onInfiniteScrollElementCreate(element) {
        const options = {
            rootMargin: "600px",
            threshold: 0,
        };

        const observer = new IntersectionObserver(infiniteScrollCallback, options);
        observer.observe(element);
    }

    function FilterEntry(item) {
        return <a
            class="!no-underline hover:!underline px-2 py-1"
            href={item.link}
            innerHTML={item.name}
            onClick={onCategoryClick.bind(null, item)}
        >
        </a>;
    }

    return <div>
        <div class="flex flex-row flex-wrap justify-center text-[1rem] relative">
            <a
                ref={ (e) => setTimeout(() => e.classList.add("opacity-100"), 30) }
                class="!text-neutral-400 !no-underline hover:!underline px-2 py-1 opacity-0 duration-600"
                href={ "#" }
            >
                Vše
            </a>

            <For each={categories.filter(i => i.parent == 0)}>
                { FilterEntry }
            </For>
        </div>
        <div class="flex flex-row flex-wrap justify-center text-[1rem] relative">
            { categoryFilterSegments().length > 1 &&
                <a
                    ref={ (e) => setTimeout(() => e.classList.add("opacity-100"), 30) }
                    class="!text-neutral-400 !no-underline hover:!underline px-2 py-1 opacity-0 duration-600"
                    href={ previousUrl() }
                >
                    { backButtonLabel() }
                </a>
            }

            <CategoryListAnimation
                enterClass="animate-fade-bottom-in"
                exitClass="animate-fade-top-out absolute"
            >
                { categoryFilterSegments().length > 0 &&
                    <For each={categories.filter(i => i.parent == currentCategory())}>
                        { FilterEntry }
                    </For>
                }
            </CategoryListAnimation>
        </div>

        <div
            class="text-center mx-auto mt-12 transition-opacity offset-500 duration-300 absolute left-1/2 -translate-x-1/2"
            classList={{ "opacity-0": !entries.loading }}
        >
            <div class="loader"></div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 mt-12 gap-x-4 gap-y-8">
            <For each={entries()}>
                {(item, _) =>
                    <a
                        key={ item.link }
                        href={ item.link }
                        class="!no-underline"
                        classList={{
                            "animate-fade-scale-out": entries.loading && !isPageLoad(),
                            "animate-fade-scale-in": !entries.loading && !isPageLoad()
                        }}
                    >
                        { item.thumbnail &&
                            <img class="w-full aspect-[5/4] object-cover mb-4 transition-opacity duration-500 hover:opacity-60" src={item.thumbnail} />
                        }

                        { !item.thumbnail &&
                            <div class="w-full aspect-[5/4] mb-4 bg-neutral-900"></div>
                        }

                        <h2 class="!text-xs" innerHTML={ item.title.rendered }></h2>
                        <p class="!text-sm">{ item.meta.author }</p>
                    </a>
                }
            </For>
        </div>

        <div ref={onInfiniteScrollElementCreate} class="w-10 h-10">
        </div>
    </div>;
}

document.addEventListener("DOMContentLoaded", () => {
    const element = document.querySelector("#portfolio-archive");
    if (!element) return;

    const categories = JSON.parse(element.dataset.categories);
    const initialPosts = JSON.parse(element.dataset.posts);

    const categoryTree = {};
    const categoriesById = {};
    for (const category of categories) {
        categoriesById[category.id] = { _id: category.id };
    }

    for (const category of categories) {
        if (category.parent == 0) {
            categoryTree[category.slug] = categoriesById[category.id];
        } else {
            categoriesById[category.parent][category.slug] = categoriesById[category.id];
        }
    }

    render(() => <Archive
        categories={categories}
        categoryTree={categoryTree}
        initialPosts={initialPosts}
        initialFilter={(window.location.hash || "#").slice(1)}
    />, element);
});
